/**
 * Compliance > CoAs > Missing CoAs (Phase 2).
 *
 * The set of purchase orders that should have a Certificate of Analysis but
 * do not. Built from Sage purchasing data (I/WIP item codes received) and
 * reconciled against the filed certificates. One row per expected PO. FSQA
 * can waive a PO (with a reason) so it stops re-appearing on every reconcile.
 *
 * Mounted as the "Missing CoAs" tab of the CoAs page; the certificate grid
 * (CoAsList) is the sibling "Certificates" tab. Mirrors the CoAsList grid for
 * sort / filter / export and the Commerce Orders page for the bulk-action bar.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Loader2, X } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { InfoTooltip, SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { CoasTabs } from './CoasTabs';
import { useSession } from '@/lib/useSession';
import { formatDateShort } from '@/lib/dateUtils';
import {
  hasComplianceCoaRead,
  hasComplianceCoaWrite,
} from '@/lib/useCoas';
import {
  downloadMissingCoasXlsx,
  missingStatusLabel,
  useMissingCoaFilters,
  useMissingCoas,
  useWaiveExpectedPo,
  type ExpectedPoRow,
  type MissingCoaFilters,
} from '@/lib/useMissingCoas';

// missing=red (action needed), not_missing=gray (satisfied), waived=slate/blue
// (deliberately cleared). Same tinted-pill family as the certificate grid.
const STATUS_TONES: Record<string, string> = {
  missing: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  not_missing: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  waived: 'bg-slate-200 text-slate-700 dark:bg-slate-700/50 dark:text-slate-200',
};

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300';
  return <Pill tone={tone}>{missingStatusLabel(status)}</Pill>;
}

// The raw item_codes string is a comma-separated list that overflows the row.
// Render a compact "N items" count in the grid and hang the full list off an
// InfoTooltip so it stays one line. Empty -> em dash.
function ItemCount({ itemCodes }: { itemCodes: string | null }) {
  const items = (itemCodes ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (items.length === 0) return <span>--</span>;
  return (
    <span className="inline-flex items-baseline text-amp-ink dark:text-amp-dark-ink">
      {items.length} item{items.length === 1 ? '' : 's'}
      <InfoTooltip label="Item codes">{items.join(', ')}</InfoTooltip>
    </span>
  );
}

// Sortable columns. Like CoAsList, the missing backend does not accept a sort
// param, so the sort runs client-side over the rows already loaded.
type MissingSortColumn =
  | 'po_number'
  | 'supplier'
  | 'last_receipt_date'
  | 'days_overdue'
  | 'status'
  | 'matched_doc_count'
  | 'item_codes';

function sortValue(row: ExpectedPoRow, col: MissingSortColumn): string | number {
  switch (col) {
    case 'po_number': return row.po_number ?? '';
    case 'supplier': return (row.supplier_name ?? '').toLowerCase();
    case 'last_receipt_date': return row.last_receipt_date ?? '';
    case 'days_overdue': return row.days_overdue ?? -1;
    case 'status': return missingStatusLabel(row.status).toLowerCase();
    case 'matched_doc_count': return row.matched_doc_count;
    case 'item_codes': return (row.item_codes ?? '').toLowerCase();
  }
}

function sortRows(rows: ExpectedPoRow[], col: MissingSortColumn, dir: SortDir): ExpectedPoRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, col);
    const bv = sortValue(b, col);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

export function MissingCoas() {
  const { user } = useSession();
  const canView = hasComplianceCoaRead(user?.permissions, user?.superAdmin);
  const canWrite = hasComplianceCoaWrite(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  // Default status is "missing" per the data contract (the work queue). An
  // explicit empty value is not offered; the operator picks a concrete status.
  const status = searchParams.get('status') ?? 'missing';
  const supplier = searchParams.get('supplier') ?? '';
  const overdueMin = searchParams.get('overdue_min') ?? '';

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const setStr = (k: string, v: string) => update((sp) => (v ? sp.set(k, v) : sp.delete(k)));
  const resetFilters = () =>
    update((sp) => ['q', 'status', 'supplier', 'overdue_min'].forEach((k) => sp.delete(k)));

  const filters: MissingCoaFilters = useMemo(
    () => ({
      q: q || undefined,
      status: status || undefined,
      supplier: supplier || undefined,
      overdue_min: overdueMin ? Number(overdueMin) : undefined,
    }),
    [q, status, supplier, overdueMin],
  );

  const missingQ = useMissingCoas(filters);
  const filtersQ = useMissingCoaFilters();
  const waive = useWaiveExpectedPo();

  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Waive modal target: a single row, the current bulk selection, or null.
  const [waiveTarget, setWaiveTarget] = useState<{ ids: number[]; label: string } | null>(null);

  const [sort, setSort] = useState<{ column: MissingSortColumn; dir: SortDir } | null>(null);
  const toggleSort = (col: MissingSortColumn) => {
    setSort((prev) => {
      if (!prev || prev.column !== col) return { column: col, dir: 'asc' };
      if (prev.dir === 'asc') return { column: col, dir: 'desc' };
      return null;
    });
  };
  const sortedFor = (col: MissingSortColumn): SortDir | null =>
    sort && sort.column === col ? sort.dir : null;

  if (user && !canView) {
    return (
      <div className="pb-20">
        <PageHeader />
        <CoasTabs />
        <AccessDeniedCard />
      </div>
    );
  }

  const rows = sort ? sortRows(missingQ.rows, sort.column, sort.dir) : missingQ.rows;
  const colSpan = canWrite ? 9 : 7;

  // Waive only makes sense on a PO that is currently flagged missing; satisfied
  // and already-waived rows are not selectable for a bulk waive.
  const selectableIds = rows.filter((r) => r.status === 'missing').map((r) => r.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelect = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };

  const onExport = async () => {
    setExporting(true);
    setToast(null);
    try {
      await downloadMissingCoasXlsx(filters);
      setToast({ kind: 'ok', text: 'Export downloaded.' });
    } catch (err) {
      setToast({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setExporting(false);
    }
  };

  const onConfirmWaive = (reason: string) => {
    if (!waiveTarget) return;
    const ids = waiveTarget.ids;
    setToast(null);
    Promise.all(ids.map((id) => waive.mutateAsync({ id, reason, waive: true })))
      .then(() => {
        setToast({ kind: 'ok', text: `Waived ${ids.length} PO${ids.length === 1 ? '' : 's'}.` });
        setSelected(new Set());
      })
      .catch((err: unknown) => {
        setToast({ kind: 'err', text: `Waive failed: ${err instanceof Error ? err.message : String(err)}` });
      })
      .finally(() => setWaiveTarget(null));
  };

  const onUnwaive = (row: ExpectedPoRow) => {
    setToast(null);
    waive.mutate(
      { id: row.id, reason: '', waive: false },
      {
        onSuccess: () => setToast({ kind: 'ok', text: `Un-waived PO ${row.po_number}.` }),
        onError: (err) => setToast({ kind: 'err', text: `Un-waive failed: ${err.message}` }),
      },
    );
  };

  const filterBar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="missing-search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="missing-search"
          value={q}
          onChange={(v) => setStr('q', v)}
          placeholder="PO, supplier..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <label htmlFor="missing-status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Status</label>
        <select
          id="missing-status"
          value={status}
          onChange={(e) => setStr('status', e.target.value)}
          className="min-w-[140px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          {(filtersQ.options?.statuses ?? [
            { value: 'missing', label: 'Missing' },
            { value: 'waived', label: 'Waived' },
          ]).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="missing-overdue" className="inline-flex items-baseline text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Min days overdue
          <InfoTooltip label="Min days overdue">Only show POs whose certificate is at least this many days overdue, counted from the last receipt date.</InfoTooltip>
        </label>
        <input
          id="missing-overdue"
          type="number"
          min={0}
          value={overdueMin}
          onChange={(e) => setStr('overdue_min', e.target.value)}
          placeholder="0"
          className="w-24 border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <span className="inline-flex items-baseline text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Supplier
          <InfoTooltip label="Supplier">Search by supplier name. The list can be long, so this filter supports text search.</InfoTooltip>
        </span>
        <SearchableSelect
          ariaLabel="Supplier filter"
          options={filtersQ.options?.suppliers ?? []}
          selected={supplier ? [supplier] : []}
          onChange={(v) => setStr('supplier', v[0] ?? '')}
          placeholder="Any"
        />
      </div>
      {(q || status !== 'missing' || supplier || overdueMin) && (
        <button
          type="button"
          onClick={resetFilters}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <PageHeader />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="coas" toolName="CoAs" />
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export to Excel
          </button>
        </div>
      </div>

      <CoasTabs />

      {toast && (
        <div
          className={`mb-4 rounded-md border px-3 py-2 text-sm ${
            toast.kind === 'ok'
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
          }`}
          role="status"
        >
          {toast.text}
        </div>
      )}

      {canWrite && selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
          <span className="text-sm text-amp-ink dark:text-amp-dark-ink">{selected.size} selected</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-white dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setWaiveTarget({ ids: Array.from(selected), label: `${selected.size} selected PO${selected.size === 1 ? '' : 's'}` })}
              className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
            >
              Waive selected ({selected.size})
            </button>
          </div>
        </div>
      )}

      <DataGridShell
        total={missingQ.totalCount}
        loaded={rows.length}
        countNoun="expected PO rows"
        toolbar={filterBar}
        onReachEnd={missingQ.fetchNextPage}
        hasMore={missingQ.hasNextPage}
        isLoadingMore={missingQ.isFetchingNextPage}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {canWrite && (
                <th className="text-left font-medium px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all missing"
                  />
                </th>
              )}
              <SortableHeader label="PO #" sorted={sortedFor('po_number')} onToggle={() => toggleSort('po_number')} thClassName="px-3 py-2" />
              <SortableHeader label="Supplier" sorted={sortedFor('supplier')} onToggle={() => toggleSort('supplier')} thClassName="px-3 py-2" />
              <SortableHeader label="Received" sorted={sortedFor('last_receipt_date')} onToggle={() => toggleSort('last_receipt_date')} tooltip="Last receipt date for the PO. A CoA is expected before the item hits the dock, so a received PO with no certificate is late." thClassName="px-3 py-2" />
              <SortableHeader label="Days Overdue" sorted={sortedFor('days_overdue')} onToggle={() => toggleSort('days_overdue')} align="right" thClassName="px-3 py-2" />
              <SortableHeader label="Status" sorted={sortedFor('status')} onToggle={() => toggleSort('status')} thClassName="px-3 py-2" />
              <SortableHeader label="Matched certs" sorted={sortedFor('matched_doc_count')} onToggle={() => toggleSort('matched_doc_count')} align="right" thClassName="px-3 py-2" />
              <SortableHeader label="Item codes" sorted={sortedFor('item_codes')} onToggle={() => toggleSort('item_codes')} thClassName="px-3 py-2" />
              {canWrite && <th className="text-left font-medium px-3 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {missingQ.isLoading ? (
              <tr><td colSpan={colSpan} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading expected POs...</td></tr>
            ) : missingQ.error ? (
              <tr><td colSpan={colSpan} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {missingQ.error.message}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colSpan} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No expected POs match this filter.</td></tr>
            ) : (
              rows.map((row) => (
                <MissingRow
                  key={row.id}
                  row={row}
                  canWrite={canWrite}
                  selected={selected.has(row.id)}
                  onToggle={() => toggleSelect(row.id)}
                  onWaive={() => setWaiveTarget({ ids: [row.id], label: `PO ${row.po_number}` })}
                  onUnwaive={() => onUnwaive(row)}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>

      {waiveTarget && (
        <WaiveModal
          label={waiveTarget.label}
          saving={waive.isPending}
          onConfirm={onConfirmWaive}
          onClose={() => setWaiveTarget(null)}
        />
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Compliance</p>
      <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">CoAs</h1>
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Purchase orders that received a CoA-required item but have no certificate filed yet. Waive a PO to clear
        it from the list when a certificate is not expected.
      </p>
    </div>
  );
}

function MissingRow({
  row,
  canWrite,
  selected,
  onToggle,
  onWaive,
  onUnwaive,
}: {
  row: ExpectedPoRow;
  canWrite: boolean;
  selected: boolean;
  onToggle: () => void;
  onWaive: () => void;
  onUnwaive: () => void;
}) {
  const navigate = useNavigate();
  const isMissing = row.status === 'missing';
  const isWaived = row.status === 'waived';
  const onRowClick = () => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    navigate({ pathname: `/compliance/coas/missing/${row.id}`, search });
  };
  return (
    <tr
      className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm cursor-pointer"
      onClick={onRowClick}
    >
      {canWrite && (
        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            disabled={!isMissing}
            onChange={onToggle}
            aria-label={`Select PO ${row.po_number}`}
          />
        </td>
      )}
      <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">{row.po_number}</td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{row.supplier_name ?? '--'}</td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(row.last_receipt_date)}</td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{row.days_overdue ?? '--'}</td>
      <td className="px-3 py-2">
        <StatusPill status={row.status} />
        {isWaived && row.waive_reason && (
          <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted truncate max-w-[200px]" title={row.waive_reason}>
            {row.waive_reason}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted">{row.matched_doc_count}</td>
      <td className="px-3 py-2 w-24 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
        <ItemCount itemCodes={row.item_codes} />
      </td>
      {canWrite && (
        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          {isMissing ? (
            <button
              type="button"
              onClick={onWaive}
              className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-white dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface"
            >
              Waive
            </button>
          ) : isWaived ? (
            <button
              type="button"
              onClick={onUnwaive}
              className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-white dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface"
            >
              Un-waive
            </button>
          ) : (
            <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
          )}
        </td>
      )}
    </tr>
  );
}

function WaiveModal({
  label,
  saving,
  onConfirm,
  onClose,
}: {
  label: string;
  saving: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">Waive {label}</h2>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
          Waiving marks the PO as not applicable so it stops appearing on the missing list. Give a short reason for the record.
        </p>
        {/* Stays a plain box. This reason goes into the COA XLSX export that
            FSQA staff open in Excel, and into a native title tooltip, neither of
            which can render HTML. The column is also only VARCHAR(500). */}
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Supplier exempt, certificate on file outside this system"
          className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={saving || !reason.trim()}
            className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            Waive
          </button>
        </div>
      </div>
    </div>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        Missing CoAs is gated by the compliance.coas access slug. Ask IT to grant you access.
      </p>
    </div>
  );
}
